import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { campaignsList, campaignCreate, campaignUpdate, campaignDelete } from "@/lib/api";
import { Loader2, Plus, Megaphone, Pencil, Trash2, Search } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? String(iso).slice(0, 10)
    : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

export const CampaignsManager = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // {id, name} or {} for create
  const [nameDraft, setNameDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [togglingId, setTogglingId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await campaignsList(token);
      setItems(data.campaigns || []);
    } catch {
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing({}); setNameDraft(""); };
  const openEdit = (c) => { setEditing(c); setNameDraft(c.name); };

  const save = async () => {
    const name = nameDraft.trim();
    if (!name) { toast.error("Campaign name is required"); return; }
    setSaving(true);
    try {
      if (editing && editing.id) {
        const res = await campaignUpdate(token, editing.id, { name });
        const cascaded = res.cascaded_prospects || 0;
        toast.success(cascaded > 0
          ? `Campaign renamed \u00b7 ${cascaded} prospect${cascaded === 1 ? "" : "s"} updated`
          : "Campaign renamed");
      } else {
        await campaignCreate(token, { name });
        toast.success("Campaign created");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save campaign");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    setTogglingId(c.id);
    try {
      await campaignUpdate(token, c.id, { active: !c.active });
      toast.success(!c.active ? "Campaign activated" : "Campaign deactivated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update campaign");
    } finally {
      setTogglingId("");
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await campaignDelete(token, deleteTarget.id);
      toast.success("Campaign deleted");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete campaign");
      setDeleteTarget(null);
    }
  };

  const filtered = items.filter((c) =>
    c.name.toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <div className="space-y-5" data-testid="campaigns-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Megaphone size={22} className="text-[#66B2FF]" /> Campaigns
          </h1>
          <p className="text-white/55 text-sm mt-1">
            Manage the campaign names used to organize prospects across the CRM.
          </p>
        </div>
        <Button className="bg-[#1E6BFF] hover:bg-[#1E6BFF]/90 h-9" onClick={openCreate} data-testid="campaign-create-btn">
          <Plus size={16} className="mr-1" /> Create Campaign
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
        <Input className="bg-[#0A0F1E] border-white/10 text-white pl-9 h-9"
          placeholder="Search campaigns..." value={search}
          onChange={(e) => setSearch(e.target.value)} data-testid="campaign-search" />
      </div>

      <div className="rounded-xl border border-white/10 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-white/50">Campaign</TableHead>
              <TableHead className="text-white/50">Status</TableHead>
              <TableHead className="text-white/50">Prospects</TableHead>
              <TableHead className="text-white/50">Created</TableHead>
              <TableHead className="text-white/50">Created By</TableHead>
              <TableHead className="text-white/50 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12"><Loader2 className="animate-spin mx-auto text-white/40" /></TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-white/40">No campaigns yet. Create one to get started.</TableCell></TableRow>
            ) : filtered.map((c) => (
              <TableRow key={c.id} className="border-white/10 hover:bg-white/5" data-testid="campaign-row">
                <TableCell className="text-white font-medium">{c.name}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Switch checked={c.active} disabled={togglingId === c.id}
                      onCheckedChange={() => toggleActive(c)} data-testid={`campaign-toggle-${c.id}`} />
                    <Badge className={`border ${c.active
                      ? "bg-[#2EE59D]/15 text-[#2EE59D] border-[#2EE59D]/30"
                      : "bg-white/5 text-white/45 border-white/15"}`}>
                      {c.active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </TableCell>
                <TableCell className="text-white/70 tabular-nums">{c.prospect_count}</TableCell>
                <TableCell className="text-white/55 text-sm">{fmtDate(c.created_at)}</TableCell>
                <TableCell className="text-white/55 text-sm">{c.created_by_name || "\u2014"}</TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    <Button size="sm" variant="ghost" className="text-white/70 hover:text-white hover:bg-white/5 h-8 w-8 p-0"
                      onClick={() => openEdit(c)} data-testid={`campaign-edit-${c.id}`} title="Rename">
                      <Pencil size={15} />
                    </Button>
                    <Button size="sm" variant="ghost"
                      className="text-[#FF5C7A] hover:text-[#FF5C7A] hover:bg-[#FF5C7A]/10 h-8 w-8 p-0 disabled:opacity-30"
                      disabled={c.prospect_count > 0}
                      onClick={() => setDeleteTarget(c)} data-testid={`campaign-delete-${c.id}`}
                      title={c.prospect_count > 0 ? "Deactivate instead \u2014 prospects are assigned" : "Delete"}>
                      <Trash2 size={15} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create / Rename dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#0F1424] border-white/10 text-white" data-testid="campaign-dialog">
          <DialogHeader>
            <DialogTitle>{editing && editing.id ? "Rename Campaign" : "Create Campaign"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-white/70 text-sm">Campaign Name</label>
            <Input autoFocus className="bg-[#0A0F1E] border-white/10 text-white"
              value={nameDraft} onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") save(); }}
              placeholder="e.g. PT Clinic Q1" data-testid="campaign-name-input" />
            {editing && editing.id && editing.prospect_count > 0 && (
              <p className="text-white/45 text-xs">
                Renaming will update {editing.prospect_count} assigned prospect{editing.prospect_count === 1 ? "" : "s"}.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)} className="text-white/60 hover:text-white hover:bg-white/5">Cancel</Button>
            <Button className="bg-[#1E6BFF] hover:bg-[#1E6BFF]/90" onClick={save} disabled={saving} data-testid="campaign-save-btn">
              {saving ? <Loader2 className="animate-spin" size={16} /> : (editing && editing.id ? "Save" : "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#0F1424] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete campaign?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              This permanently removes &ldquo;{deleteTarget?.name}&rdquo;. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/15 text-white/80 hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-[#FF5C7A] hover:bg-[#FF5C7A]/90 text-white" onClick={doDelete} data-testid="campaign-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CampaignsManager;
